import '@amzn/awsui-global-styles/polaris.css';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { LoadingBar } from './components/common/LoadingBar';
import App from './components/root/App';
import './i18n';
import './index.css';
import reportWebVitals from './reportWebVitals';

import { ApiClientProvider } from './store/api.context';
import { FlashbarProvider } from './store/flashbar.context';
import { ThemeProvider } from './store/theme.context';
import { UserProvider } from './store/user.context';

import { Amplify } from '@aws-amplify/core';
import amplifyConfig from './config/amplifyConfig';

import { AuthClientProvider } from './store/auth.context';
import { ChallengesProvider } from './store/challenge.context';
import { CookieConsentProvider } from './store/cookie-consent.context';
import { EditEventProvider } from './store/edit-event.context';
import { EventsProvider } from './store/events.context';
import { SplitPanelProvider } from './store/split-panel.context';
import { ToolPanelProvider } from './store/tool-panel.context';

import { BASE_PATH_PREFIX } from './utils/env.utils';

import { ErrorBoundary } from './components/root/ErrorBoundary/ErrorBoundary';
import { HelmetProvider } from 'react-helmet-async';
import { ChallengeTutorial } from './components/challenges/ChallengeTutorial';
import { CampaignsProvider } from './store/campaigns.context';
import { ChallengeSetProvider } from './store/challenge-set.context';
import { CreateChallengeProvider } from './store/create-challenge.context';
import { EditCampaignProvider } from './store/edit-campaign.context';
import { EventTemplateChallengesProvider } from './store/event-template-challenges.context';
import { JamChallengeRecentFeedbackProvider } from './store/jam-challenge-recent-feedback';
import { JamChallengeProvider } from './store/jam-challenge.context';
import { JamFeedbackProvider } from './store/jam-feedback';
import { JamMyTeamProvider } from './store/jam-myteam.context';
import { LSEProvider } from './store/lse.context';
import { WebsocketProvider } from './store/websocket.context';

import { IconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import { faCaretDown, faTrophy, faUser } from '@fortawesome/free-solid-svg-icons';

import { JamReportProvider } from '@/src/store/jam-report.context';
import { JamChallengeDetailsProvider } from './store/jam-challenge-details.context';
import { JamChatProvider } from './store/jam-chat.context';
import { JamEventDetailsProvider } from './store/jam-event-details.context';
import { JamFacilitatorProvider } from './store/jam-facilitator.context';
import { JamLeaderboardProvider } from './store/jam-leaderboard.context';

import NiceModal from '@ebay/nice-modal-react';
library.add(faCaretDown as IconDefinition);
library.add(faUser as IconDefinition);
library.add(faTrophy as IconDefinition);

Amplify.configure(amplifyConfig);

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
global.fetch = require('node-fetch');

const Root = () => (
  <React.StrictMode>
    <BrowserRouter basename={BASE_PATH_PREFIX}>
      <ErrorBoundary componentName="AppRoot">
        <div className="awsui">
          <Suspense fallback={<LoadingBar />}>
            <FlashbarProvider>
              <AuthClientProvider>
                <ApiClientProvider>
                  <UserProvider>
                    {/*
                      TODO: figure out how to wrap a provider around a specific route in routes.ts or MainContent.tsx
                            because this EventsProvider is only needed by EventList and EventDetails
                    */}
                    <CookieConsentProvider>
                      <LSEProvider>
                        <ChallengesProvider>
                          <ChallengeSetProvider>
                            <SplitPanelProvider>
                              <ToolPanelProvider>
                                <EventsProvider>
                                  <EventTemplateChallengesProvider>
                                    <CampaignsProvider>
                                      <EditEventProvider>
                                        <EditCampaignProvider>
                                          <CreateChallengeProvider>
                                            <JamEventDetailsProvider>
                                              <JamLeaderboardProvider>
                                                <JamChallengeProvider>
                                                  <JamChallengeDetailsProvider>
                                                    <JamFeedbackProvider>
                                                      <JamFacilitatorProvider>
                                                        <JamReportProvider>
                                                          <JamChallengeRecentFeedbackProvider>
                                                            <ChallengeTutorial>
                                                              <JamMyTeamProvider>
                                                                <JamChatProvider>
                                                                  <WebsocketProvider>
                                                                    <HelmetProvider>
                                                                      <ThemeProvider>
                                                                        <NiceModal.Provider>
                                                                          <App />
                                                                        </NiceModal.Provider>
                                                                      </ThemeProvider>
                                                                    </HelmetProvider>
                                                                  </WebsocketProvider>
                                                                </JamChatProvider>
                                                              </JamMyTeamProvider>
                                                            </ChallengeTutorial>
                                                          </JamChallengeRecentFeedbackProvider>
                                                        </JamReportProvider>
                                                      </JamFacilitatorProvider>
                                                    </JamFeedbackProvider>
                                                  </JamChallengeDetailsProvider>
                                                </JamChallengeProvider>
                                              </JamLeaderboardProvider>
                                            </JamEventDetailsProvider>
                                          </CreateChallengeProvider>
                                        </EditCampaignProvider>
                                      </EditEventProvider>
                                    </CampaignsProvider>
                                  </EventTemplateChallengesProvider>
                                </EventsProvider>
                              </ToolPanelProvider>
                            </SplitPanelProvider>
                          </ChallengeSetProvider>
                        </ChallengesProvider>
                      </LSEProvider>
                    </CookieConsentProvider>
                  </UserProvider>
                </ApiClientProvider>
              </AuthClientProvider>
            </FlashbarProvider>
          </Suspense>
        </div>
      </ErrorBoundary>
    </BrowserRouter>
  </React.StrictMode>
);

ReactDOM.render(<Root />, document.getElementById('root'));

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
reportWebVitals();
